import { useEffect, useRef, useState } from "react";
import { ShieldCheck, ShieldAlert, X, Loader2, KeyRound, Mail, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useSuperMode } from "../../context/SuperModeContext";
import { formatApiError } from "../../lib/api";

/**
 * SuperModePinModal — single modal that adapts to four flows:
 *   1) UNLOCK      → MPIN exists, ask for it.
 *   2) SETUP_OTP   → no MPIN yet, request OTP to recovery email.
 *   3) SETUP_PIN   → after OTP, enter OTP + new MPIN twice.
 *   4) RESET_*     → same as SETUP but for "Forgot MPIN".
 */
export default function SuperModePinModal({ open, onClose }) {
    const sm = useSuperMode();
    const [view, setView] = useState("unlock"); // unlock | setup_otp | setup_pin | reset_otp | reset_pin
    const [mpin, setMpin] = useState("");
    const [newMpin, setNewMpin] = useState("");
    const [confirmMpin, setConfirmMpin] = useState("");
    const [otp, setOtp] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const inputRef = useRef(null);

    // Initial view depends on whether MPIN is already set
    useEffect(() => {
        if (!open) return;
        setMpin(""); setNewMpin(""); setConfirmMpin(""); setOtp(""); setError("");
        setView(sm.pinSet ? "unlock" : "setup_otp");
    }, [open, sm.pinSet]);

    useEffect(() => {
        if (open) setTimeout(() => inputRef.current?.focus(), 80);
    }, [open, view]);

    if (!open) return null;

    const handleUnlock = async (e) => {
        e?.preventDefault?.();
        setError("");
        if (!/^\d{6}$/.test(mpin)) { setError("Enter your 6-digit MPIN."); return; }
        setBusy(true);
        try {
            await sm.unlock(mpin);
            toast.success("Super Mode activated", { description: `Session valid for ${sm.sessionTtlMinutes} minutes` });
            onClose();
        } catch (err) {
            const msg = formatApiError(err.response?.data?.detail) || "Unable to unlock";
            setError(msg);
        } finally { setBusy(false); }
    };

    const handleRequestOtp = async (kind) => {
        setError(""); setBusy(true);
        try {
            const fn = kind === "reset" ? sm.requestResetOtp : sm.requestSetupOtp;
            const res = await fn();
            toast.success("OTP sent", { description: `Check ${res.delivered_to || "your inbox"}` });
            setView(kind === "reset" ? "reset_pin" : "setup_pin");
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || "Could not send OTP");
        } finally { setBusy(false); }
    };

    const handleSetOrReset = async (kind, e) => {
        e?.preventDefault?.();
        setError("");
        if (!/^\d{6}$/.test(otp)) { setError("OTP must be 6 digits."); return; }
        if (!/^\d{6}$/.test(newMpin)) { setError("MPIN must be 6 digits."); return; }
        if (newMpin !== confirmMpin) { setError("MPINs do not match."); return; }
        setBusy(true);
        try {
            const fn = kind === "reset" ? sm.resetMpin : sm.setupMpin;
            await fn(otp, newMpin);
            toast.success(kind === "reset" ? "MPIN reset successfully" : "MPIN created. You can now unlock Super Mode.");
            setView("unlock");
            setMpin(""); setOtp(""); setNewMpin(""); setConfirmMpin("");
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || "Could not save MPIN");
        } finally { setBusy(false); }
    };

    const inputClass = "w-full text-center text-2xl tracking-[0.5em] font-mono px-3 py-3 rounded-xl border-2 border-slate-200 bg-white focus:outline-none focus:border-[#1E3A8A] focus:ring-2 focus:ring-[#1E3A8A]/15";

    return (
        <div className="fixed inset-0 z-[120] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-150" data-testid="super-mode-modal">
            <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden" role="dialog" aria-modal="true">
                <div className="bg-gradient-to-r from-[#1E3A8A] to-[#7C3AED] text-white px-5 py-4 flex items-center gap-3">
                    <ShieldCheck className="w-6 h-6" />
                    <div className="flex-1">
                        <div className="text-[10px] tracking-[0.2em] uppercase opacity-80">Anand Tutorials</div>
                        <div className="text-base font-bold">
                            {view === "unlock" && "Unlock Super Admin Mode"}
                            {view === "setup_otp" && "Set up MPIN"}
                            {view === "setup_pin" && "Choose your 6-digit MPIN"}
                            {view === "reset_otp" && "Reset MPIN"}
                            {view === "reset_pin" && "Create new MPIN"}
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/20 transition" aria-label="Close" data-testid="super-mode-modal-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-5 space-y-4">
                    {error && (
                        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 inline-flex items-start gap-2" data-testid="super-mode-error">
                            <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" /> <span>{error}</span>
                        </div>
                    )}

                    {/* ----- Unlock view ----- */}
                    {view === "unlock" && (
                        <form onSubmit={handleUnlock} className="space-y-3" data-testid="super-mode-unlock-form">
                            <p className="text-xs text-slate-600">Enter your 6-digit MPIN to reveal all admin tools. Session expires after {sm.sessionTtlMinutes} minutes of inactivity.</p>
                            <input
                                ref={inputRef}
                                type="password"
                                inputMode="numeric"
                                autoComplete="off"
                                maxLength={6}
                                value={mpin}
                                onChange={(e) => setMpin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                placeholder="••••••"
                                className={inputClass}
                                data-testid="super-mode-mpin-input"
                            />
                            <button
                                type="submit"
                                disabled={busy || mpin.length !== 6}
                                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#1E3A8A] text-white font-semibold hover:bg-[#1E40AF] disabled:opacity-50 disabled:cursor-not-allowed transition"
                                data-testid="super-mode-unlock-btn"
                            >
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Unlock
                            </button>
                            <button
                                type="button"
                                onClick={() => setView("reset_otp")}
                                className="w-full text-xs text-slate-500 hover:text-[#1E3A8A] underline underline-offset-2"
                                data-testid="super-mode-forgot-btn"
                            >
                                Forgot MPIN?
                            </button>
                        </form>
                    )}

                    {/* ----- Setup OTP view ----- */}
                    {(view === "setup_otp" || view === "reset_otp") && (
                        <div className="space-y-3" data-testid={`super-mode-${view === "setup_otp" ? "setup" : "reset"}-otp-view`}>
                            <p className="text-xs text-slate-600">
                                We will email a 6-digit one-time code to your registered super-admin address.
                            </p>
                            <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 inline-flex items-center gap-2 text-xs text-slate-700">
                                <Mail className="w-3.5 h-3.5 text-[#1E3A8A]" />
                                <span className="font-mono">{sm.recoveryEmail || "iamanandrajput@gmail.com"}</span>
                            </div>
                            <button
                                type="button"
                                onClick={() => handleRequestOtp(view === "setup_otp" ? "setup" : "reset")}
                                disabled={busy}
                                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#1E3A8A] text-white font-semibold hover:bg-[#1E40AF] disabled:opacity-50 transition"
                                data-testid={`super-mode-${view === "setup_otp" ? "setup" : "reset"}-send-otp-btn`}
                            >
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Send OTP
                            </button>
                            {view === "reset_otp" && (
                                <button type="button" onClick={() => setView("unlock")} className="w-full text-xs text-slate-500 hover:text-[#1E3A8A] underline underline-offset-2 inline-flex items-center justify-center gap-1">
                                    <ArrowLeft className="w-3 h-3" /> Back to unlock
                                </button>
                            )}
                        </div>
                    )}

                    {/* ----- Setup/Reset PIN view (OTP + new MPIN) ----- */}
                    {(view === "setup_pin" || view === "reset_pin") && (
                        <form onSubmit={(e) => handleSetOrReset(view === "reset_pin" ? "reset" : "setup", e)} className="space-y-3" data-testid={`super-mode-${view === "setup_pin" ? "setup" : "reset"}-pin-form`}>
                            <div>
                                <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">One-time code</label>
                                <input
                                    ref={inputRef}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={6}
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                    className={inputClass}
                                    placeholder="000000"
                                    data-testid="super-mode-otp-input"
                                />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">New 6-digit MPIN</label>
                                <input
                                    type="password"
                                    inputMode="numeric"
                                    autoComplete="new-password"
                                    maxLength={6}
                                    value={newMpin}
                                    onChange={(e) => setNewMpin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                    className={inputClass}
                                    placeholder="••••••"
                                    data-testid="super-mode-new-mpin-input"
                                />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Confirm MPIN</label>
                                <input
                                    type="password"
                                    inputMode="numeric"
                                    autoComplete="new-password"
                                    maxLength={6}
                                    value={confirmMpin}
                                    onChange={(e) => setConfirmMpin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                    className={inputClass}
                                    placeholder="••••••"
                                    data-testid="super-mode-confirm-mpin-input"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={busy}
                                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#1E3A8A] text-white font-semibold hover:bg-[#1E40AF] disabled:opacity-50 transition"
                                data-testid={`super-mode-${view === "setup_pin" ? "setup" : "reset"}-save-btn`}
                            >
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                                {view === "reset_pin" ? "Reset MPIN" : "Save MPIN"}
                            </button>
                            <button type="button" onClick={() => setView(view === "reset_pin" ? "reset_otp" : "setup_otp")} className="w-full text-xs text-slate-500 hover:text-[#1E3A8A] inline-flex items-center justify-center gap-1">
                                <ArrowLeft className="w-3 h-3" /> Resend OTP
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
